/**
 * Admin-managed storefront navigation — the Shop / Deals / Brands mega menus
 * and any other header or footer entry.
 *
 * Distinct from `Menu`, which drives the ADMIN PANEL sidebar and is
 * permission-scoped. This one is public marketing navigation.
 *
 * Three-level tree: Menu → Column → Link (see `StorefrontMenuKind`). A Menu
 * with children renders as a mega menu; a Menu with a `url` and no children
 * renders as a plain bar link.
 */
export interface StorefrontMenu {
  storefrontMenuId?: string;
  tenantId?: string;
  /** Undefined/empty for a top-level entry. */
  parentId?: string;
  /** See `StorefrontMenuKind`. */
  kind?: number;
  /** "header" (default) or "footer". */
  placement?: string;
  label?: string;
  /**
   * Target path, e.g. "/shop?sale=true". Relative paths route in-app; absolute
   * http(s) URLs render as external links.
   */
  url?: string;
  description?: string;
  /** Small pill, e.g. "Hot", "New". */
  badge?: string;
  iconName?: string;
  imageUrl?: string;
  sortOrder?: number;
  openInNewTab?: boolean;
  /** Admin enable/disable. Inactive rows are omitted from the public feed. */
  isActive?: boolean;
  /** Populated by the tree endpoints; empty on flat CRUD reads. */
  children?: StorefrontMenu[];
}

export const StorefrontMenuKind = {
  Menu: 1,
  Column: 2,
  Link: 3,
} as const;

export const StorefrontMenuKindLabels: Record<number, string> = {
  1: 'Menu',
  2: 'Column',
  3: 'Link',
};

export type StorefrontMenuCreate = Omit<
  StorefrontMenu,
  'storefrontMenuId' | 'tenantId' | 'children'
>;

export type StorefrontMenuFilter = Partial<
  Pick<StorefrontMenu, 'storefrontMenuId' | 'parentId' | 'kind' | 'placement' | 'isActive'>
>;
